import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Plus, RefreshCw, Star } from 'lucide-react';
import { Table, toast } from '../../ui';
import dashboardService from '../../services/dashboardService';
import fulfillmentService from '../../services/fulfillmentService';
import invoiceService from '../../services/invoiceService';
import { formatDate, formatDateTime, formatINR } from '../../utils/format';
import {
  AdminHeader,
  LoadError,
  Loading,
  StateBadge,
  StatTile,
  resolveOrderState,
  resolvePaymentState,
} from './pages/orderAdmin';

const count = value => (Number.isFinite(Number(value)) ? Number(value).toLocaleString('en-IN') : '—');
const money = value => (value === undefined || value === null ? '—' : formatINR(value));
const ordersLink = state => `/admin/orders?state=${state}`;

const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [delivery, setDelivery] = useState(null);

  const load = useCallback(async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    const [response, attention] = await Promise.all([
      dashboardService.getDashboardStats(),
      fulfillmentService.getNeedsAttention(200),
    ]);
    setDelivery(attention.success ? attention.data : null);
    if (response.success && response.data?.data) {
      setData(response.data.data);
      setError('');
    } else if (initial) {
      setError(response.error || "Couldn't load the dashboard");
    } else {
      toast.error(response.error || "Couldn't refresh the dashboard");
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load({ initial: true });
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    const result = await invoiceService.exportCsv();
    setExporting(false);
    if (result.success) toast.success('Invoice CSV downloaded');
    else toast.error(result.error);
  };

  if (loading) return <Loading label="Loading dashboard" />;

  if (error || !data) {
    return (
      <div className="oa-page">
        <LoadError
          title="Couldn't load the dashboard"
          message={error || 'No data came back.'}
          onRetry={() => load({ initial: true })}
        />
      </div>
    );
  }

  const awaitingVerification = data.totalAwaitingVerificationOrders;
  const paidUndelivered = data.totalPaidUndeliveredOrders;

  const orderColumns = [
    {
      title: 'Order',
      key: 'order',
      width: 200,
      render: (_, record) => (
        <div>
          <Link className="oa-link oa-mono" to={`/admin/orders/${encodeURIComponent(record.orderId)}`}>
            {record.orderId}
          </Link>
          <span className="oa-sub">{formatDateTime(record.createdAt)}</span>
        </div>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 170,
      render: (_, record) => (
        <div>
          <div>{record.userName || '—'}</div>
          <span className="oa-sub">{record.userEmail || record.userPhone || ''}</span>
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 100,
      align: 'right',
      render: amount => <span className="oa-num oa-strong">{formatINR(amount)}</span>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 170,
      render: (_, record) => <StateBadge state={resolveOrderState(record)} />,
    },
  ];

  const paymentColumns = [
    {
      title: 'Payment',
      key: 'payment',
      width: 210,
      render: (_, record) => (
        <div>
          <span className="oa-mono">{record.transactionId || record.id}</span>
          <span className="oa-sub">{formatDateTime(record.paymentDate)}</span>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'right',
      render: amount => <span className="oa-num oa-strong">{formatINR(amount)}</span>,
    },
    {
      title: 'State',
      key: 'state',
      width: 130,
      render: (_, record) => <StateBadge state={resolvePaymentState(record)} kind="payment" />,
    },
    {
      title: 'Order',
      key: 'order',
      width: 110,
      render: (_, record) =>
        record.orderId ? (
          <Link
            className="pk-btn pk-btn--secondary pk-btn--xs"
            to={`/admin/orders/${encodeURIComponent(record.orderId)}`}
            aria-label={`View order ${record.orderId}`}
          >
            View
          </Link>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <div className="oa-page">
      <AdminHeader
        title="Dashboard"
        sub="Store overview. Revenue counts paid orders only; “today” is the current day in India (IST)."
        actions={
          <>
            <button
              type="button"
              className="pk-btn pk-btn--ghost pk-btn--sm"
              onClick={() => load()}
              disabled={refreshing}
              aria-busy={refreshing || undefined}
            >
              <RefreshCw aria-hidden="true" /> Refresh
            </button>
            <button
              type="button"
              className="pk-btn pk-btn--secondary pk-btn--sm"
              onClick={handleExport}
              disabled={exporting}
              aria-busy={exporting || undefined}
              title="Every GST invoice line, for filing"
            >
              <Download aria-hidden="true" /> Export invoices (CSV)
            </button>
            <Link className="pk-btn pk-btn--primary pk-btn--sm" to="/admin/products">
              <Plus aria-hidden="true" /> Add product
            </Link>
          </>
        }
      />

      <section className="oa-stack" aria-labelledby="dash-attention">
        <h2 className="oa-section-title" id="dash-attention">
          Needs your attention
        </h2>
        <div className="oa-stats">
          <StatTile
            label="Awaiting verification"
            value={count(awaitingVerification)}
            hint="UPI payments to approve or reject"
            to={ordersLink('PaymentSubmitted')}
            attention={awaitingVerification > 0}
          />
          <StatTile
            label="Paid, not delivered"
            value={count(paidUndelivered)}
            hint="Check delivery"
            to={ordersLink('Paid')}
            attention={paidUndelivered > 0}
          />
          <StatTile
            label="Delivery issues"
            value={delivery ? count(delivery.count) : '—'}
            hint={delivery ? `${count(delivery.needsAdmin)} need you` : 'Paid orders with delivery problems'}
            to="/admin/delivery-issues"
            attention={delivery?.needsAdmin > 0}
          />
        </div>
      </section>

      <section className="oa-stack" aria-labelledby="dash-revenue">
        <h2 className="oa-section-title" id="dash-revenue">
          Revenue from paid orders
        </h2>
        <div className="oa-stats">
          <StatTile label="All time" value={money(data.totalOrderAmount)} hint={`${count(data.totalSuccessOrders)} paid orders`} />
          <StatTile label="Last 7 days" value={money(data.lastWeekRevenue)} />
          <StatTile label="Today" value={money(data.todayRevenue)} />
          <StatTile label="Average paid order" value={money(data.averageOrderValue)} />
        </div>
      </section>

      <section className="oa-stack" aria-labelledby="dash-orders">
        <h2 className="oa-section-title" id="dash-orders">
          Orders by status
        </h2>
        <div className="oa-stats">
          <StatTile
            label="All orders"
            value={count(data.totalOrders)}
            hint={`${count(data.todayOrders)} today · ${count(data.lastWeekOrders)} in 7 days`}
            to="/admin/orders"
          />
          <StatTile label="Awaiting payment" value={count(data.totalPendingOrders)} to={ordersLink('PendingPayment')} />
          <StatTile label="Delivered" value={count(data.totalDeliveredOrders)} to={ordersLink('Fulfilled')} />
          <StatTile label="Payment failed" value={count(data.totalFailedOrders)} to={ordersLink('PaymentFailed')} />
          <StatTile label="Cancelled" value={count(data.totalCancelledOrders)} to={ordersLink('Cancelled')} />
          <StatTile label="Refunded" value={count(data.totalRefundedOrders)} to={ordersLink('Refunded')} />
        </div>
      </section>

      <section className="oa-stack" aria-labelledby="dash-payments">
        <h2 className="oa-section-title" id="dash-payments">
          Payments
        </h2>
        <div className="oa-stats">
          <StatTile
            label="Paid"
            value={count(data.totalSuccessPayments)}
            hint={`${money(data.totalPaidAmount)} received`}
            to="/admin/transactions"
          />
          <StatTile label="Needs review" value={count(data.totalSubmittedPayments)} to="/admin/transactions" />
          <StatTile label="Pending" value={count(data.totalPendingPayments)} hint="No proof submitted yet" />
          <StatTile label="Failed or rejected" value={count(data.totalFailedPayments)} />
        </div>
      </section>

      <section className="oa-stack" aria-labelledby="dash-catalogue">
        <h2 className="oa-section-title" id="dash-catalogue">
          Catalogue and customers
        </h2>
        <div className="oa-stats">
          <StatTile
            label="Products"
            value={count(data.totalProducts)}
            hint={data.activeProducts !== undefined ? `${count(data.activeProducts)} active` : undefined}
            to="/admin/products"
          />
          <StatTile label="Deliverables" value={count(data.totalDeliverables)} hint="Products with deliverables" to="/admin/deliverables" />
          <StatTile label="Users" value={count(data.totalUsers)} hint={`${count(data.todayUsers)} joined today`} to="/admin/users" />
          <StatTile
            label="Reviews"
            value={count(data.totalReviews)}
            hint={data.averageRating ? `Average ${Number(data.averageRating).toFixed(1)} / 5` : undefined}
            to="/admin/reviews"
          />
          <StatTile label="Videos" value={count(data.totalProjectVideos)} to="/admin/videos" />
          <StatTile label="GitHub codes" value={count(data.totalGithubCodes)} to="/admin/github-codes" />
        </div>
      </section>

      <div className="oa-split">
        <div className="oa-col">
          <section className="pk-card oa-panel oa-panel--flush" aria-labelledby="dash-latest-orders">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="dash-latest-orders">
                Latest orders
              </h2>
              <Link className="oa-link oa-small" to="/admin/orders">
                All orders
              </Link>
            </div>
            <Table
              dataSource={data.latestOrders || []}
              columns={orderColumns}
              pagination={false}
              rowKey="orderId"
              scroll={{ x: 640 }}
              emptyText="No orders yet."
            />
          </section>

          <section className="pk-card oa-panel oa-panel--flush" aria-labelledby="dash-latest-payments">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="dash-latest-payments">
                Latest payments
              </h2>
              <Link className="oa-link oa-small" to="/admin/transactions">
                All transactions
              </Link>
            </div>
            <Table
              dataSource={data.latestPayments || []}
              columns={paymentColumns}
              pagination={false}
              rowKey="id"
              scroll={{ x: 560 }}
              emptyText="No payments yet."
            />
          </section>
        </div>

        <div className="oa-col">
          <section className="pk-card oa-panel" aria-labelledby="dash-latest-users">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="dash-latest-users">
                New users
              </h2>
              <Link className="oa-link oa-small" to="/admin/users">
                All users
              </Link>
            </div>
            {data.latestUsers?.length ? (
              <ul className="oa-list">
                {data.latestUsers.slice(0, 6).map(user => (
                  <li key={user.id || user.email}>
                    <div className="oa-list__main">
                      <div className="oa-strong">{user.fullName || 'No name'}</div>
                      <span className="oa-sub">
                        {user.email || 'No email'} · joined {formatDate(user.createdDate)}
                      </span>
                    </div>
                    <span className="pk-badge" data-tone={user.role === 'Admin' ? 'info' : undefined}>
                      {user.role || 'User'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="oa-muted oa-small">No users yet.</p>
            )}
          </section>

          <section className="pk-card oa-panel" aria-labelledby="dash-latest-reviews">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="dash-latest-reviews">
                Latest reviews
              </h2>
              <Link className="oa-link oa-small" to="/admin/reviews">
                All reviews
              </Link>
            </div>
            {data.latestReviews?.length ? (
              <ul className="oa-list">
                {data.latestReviews.slice(0, 5).map((review, index) => (
                  <li key={review.id || index}>
                    <div className="oa-list__main">
                      <div className="oa-strong">{review.userName || 'Customer'}</div>
                      <span className="oa-sub">{review.comment || 'No comment'}</span>
                    </div>
                    <span className="pk-badge" aria-label={`${review.rating} out of 5`}>
                      <Star aria-hidden="true" /> {review.rating}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="oa-muted oa-small">No reviews yet.</p>
            )}
          </section>

          <section className="pk-card oa-panel" aria-labelledby="dash-latest-products">
            <div className="oa-panel__head">
              <h2 className="oa-panel__title" id="dash-latest-products">
                Latest products
              </h2>
              <Link className="oa-link oa-small" to="/admin/products">
                All products
              </Link>
            </div>
            {data.latestProducts?.length ? (
              <ul className="oa-list">
                {data.latestProducts.slice(0, 5).map(product => (
                  <li key={product.id}>
                    <div className="oa-list__main">
                      <div className="oa-strong">{product.name}</div>
                      <span className="oa-sub">{formatINR(product.price)}</span>
                    </div>
                    <span className="pk-badge" data-tone={product.isActive ? 'success' : undefined}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="oa-muted oa-small">No products yet.</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
